import {
  forkTabIdentity,
  isSharedKey,
  isTabIsolationEnabled,
  isTabScopedKey,
  keyBelongsToTab,
  namespacedKey,
  peekInheritedTabId,
  rekeyToTab,
  resetTabIdentity,
  resolveTabId,
  tabIdFromKey
} from "@/store/tabStore/identity";
import {
  BACKGROUND_CLEANUP_INTERVAL_MS,
  IDLE_CLEANUP_TIMEOUT_MS,
  KEEP_ALIVE_INTERVAL_MS,
  MIGRATION_LOCK_KEY,
  MIGRATION_LOCK_TTL_MS,
  STALE_GRACE_MS,
  STORAGE_MODE_KEY,
  TAB_REGISTRY_KEY,
  VISIBILITY_SWEEP_THROTTLE_MS
} from "@/store/tabStore/keys";
import {
  isTabIdClaimed,
  pingLiveTabs,
  setupPresenceChannel
} from "@/store/tabStore/presence";
import { tabStorageReadyGate } from "@/store/tabStore/readyGate";
import {
  listKeys,
  safeGetItem,
  safeRemoveItem,
  safeSetItem,
  sharedStorage
} from "@/store/tabStore/storage";
import {
  MigrationLock,
  StorageModeEnum,
  TabRegistry
} from "@/store/tabStore/types";

// ── Module state (single tab lifetime) ─────────────────────────────────────

let keepAliveTimer: ReturnType<typeof setInterval> | null = null;
let cleanupTimer: ReturnType<typeof setInterval> | null = null;
let lastSweepTs = 0;

// ── Registry + keep-alive ───────────────────────────────────────────────────
const readRegistry = async (): Promise<TabRegistry> =>
  (await safeGetItem<TabRegistry>(TAB_REGISTRY_KEY)) ?? {};

const touchRegistry = async (tabId: string): Promise<void> => {
  const registry = await readRegistry();
  registry[tabId] = Date.now();
  await safeSetItem(TAB_REGISTRY_KEY, registry);
};

const startKeepAlive = (tabId: string): void => {
  if (keepAliveTimer) clearInterval(keepAliveTimer);
  keepAliveTimer = setInterval(() => {
    void touchRegistry(tabId);
  }, KEEP_ALIVE_INTERVAL_MS);
};

// ── Dead-tab cleanup sweep ──────────────────────────────────────────────────

/** Delete every namespaced key belonging to `tabId`. */
const deleteTabKeys = async (
  tabId: string,
  allKeys: readonly string[]
): Promise<void> => {
  const victims = allKeys.filter((key) => keyBelongsToTab(key, tabId));
  await Promise.all(victims.map((key) => safeRemoveItem(key)));
};

/**
 * One sweep pass: (1) cheap pre-check on registry staleness + orphan keys,
 * (2) PING/PONG to exclude live tabs, (3) delete dead tabs' keys FIRST then
 * their registry entry (a crash in between still flags them for the next sweep).
 */
const runCleanupSweep = async (selfId: string): Promise<void> => {
  lastSweepTs = Date.now();

  const registry = await readRegistry();
  const allKeys = await listKeys();
  const now = Date.now();

  // Tab ids that own keys right now (used to detect orphans).
  const keyTabIds = new Set<string>();
  for (const key of allKeys) {
    const tid = tabIdFromKey(key);
    if (tid) keyTabIds.add(tid);
  }

  const candidates = new Set<string>();
  // Stale registry entries.
  for (const [tid, lastSeen] of Object.entries(registry)) {
    if (tid !== selfId && now - lastSeen >= STALE_GRACE_MS) candidates.add(tid);
  }
  // Orphan keys with no registry entry at all.
  for (const tid of keyTabIds) {
    if (tid !== selfId && !(tid in registry)) candidates.add(tid);
  }

  if (candidates.size === 0) return;

  const alive = await pingLiveTabs(selfId);
  const dead = [...candidates].filter((tid) => !alive.has(tid));
  if (dead.length === 0) return;

  // 1) keys first
  for (const tid of dead) {
    await deleteTabKeys(tid, allKeys);
  }
  // 2) then registry entries (re-read to avoid clobbering fresh keep-alives)
  const fresh = await readRegistry();
  for (const tid of dead) delete fresh[tid];
  await safeSetItem(TAB_REGISTRY_KEY, fresh);
};

const scheduleBackgroundCleanup = (selfId: string): void => {
  const run = () => {
    void runCleanupSweep(selfId);
  };

  // Boot sweep when idle (forced within IDLE_CLEANUP_TIMEOUT_MS).
  if (typeof requestIdleCallback !== "undefined") {
    requestIdleCallback(run, { timeout: IDLE_CLEANUP_TIMEOUT_MS });
  } else {
    setTimeout(run, IDLE_CLEANUP_TIMEOUT_MS);
  }

  // Periodic sweep.
  if (cleanupTimer) clearInterval(cleanupTimer);
  cleanupTimer = setInterval(run, BACKGROUND_CLEANUP_INTERVAL_MS);

  // Opportunistic sweep when the user returns to this tab (throttled). Does NOT
  // shorten the grace period — a tab still must be stale AND silent to PING.
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (
        document.visibilityState === "visible" &&
        Date.now() - lastSweepTs > VISIBILITY_SWEEP_THROTTLE_MS
      ) {
        run();
      }
    });
  }
};

// ── Duplicate-tab detection ─────────────────────────────────────────────────

/**
 * Copy every key of `sourceId` into `targetId`'s namespace, so the forked tab
 * rehydrates the same state its source is showing. Sequential: these are the
 * heaviest values in the DB and this runs before first render.
 */
const cloneTabKeys = async (
  sourceId: string,
  targetId: string
): Promise<void> => {
  const keys = await listKeys();
  for (const key of keys.filter((key) => keyBelongsToTab(key, sourceId))) {
    const value = await safeGetItem(key);
    if (value == null) continue;
    await safeSetItem(rekeyToTab(key, sourceId, targetId), value);
  }
};

/**
 * Decide which id this tab owns, before any namespaced key is read or written.
 *
 * Duplicating a tab clones `window.name` AND `sessionStorage`, so the new tab boots
 * on the source's id and writes into its namespace — that is why edits showed up in
 * both tabs. A live answer to the inherited id means we are that duplicate: fork to
 * a new id and copy the source's keys once, then the two tabs never meet again.
 */
const claimTabIdentity = async (): Promise<string> => {
  const inheritedId = peekInheritedTabId();
  // Nothing inherited: a new tab, `resolveTabId` mints an id.
  if (!inheritedId) return resolveTabId();

  // Nobody answers: the previous page is gone (refresh, restored session,
  // redirect back). Same tab, so keep the id and its state.
  if (!(await isTabIdClaimed(inheritedId))) return resolveTabId();

  const forkedId = forkTabIdentity();
  await cloneTabKeys(inheritedId, forkedId);
  return forkedId;
};

// ── Migration (OFF <-> ON) ──────────────────────────────────────────────────
const readMode = async (): Promise<StorageModeEnum | null> =>
  safeGetItem<StorageModeEnum>(STORAGE_MODE_KEY);

/**
 * Best-effort single-writer lock (localforage has no atomic CAS, so it only
 * reduces contention — migrations are idempotent). True if we may write.
 */
const acquireMigrationLock = async (owner: string): Promise<boolean> => {
  const existing = await safeGetItem<MigrationLock>(MIGRATION_LOCK_KEY);
  const now = Date.now();
  if (existing && now - existing.ts < MIGRATION_LOCK_TTL_MS) return false;
  return safeSetItem<MigrationLock>(MIGRATION_LOCK_KEY, { ts: now, owner });
};

const releaseMigrationLock = async (): Promise<void> => {
  await safeRemoveItem(MIGRATION_LOCK_KEY);
};

/**
 * OFF -> ON: rename each non-preserved key into the tab namespace, per-slice
 * atomic and idempotent. On failure (e.g. QuotaExceeded) it throws so the caller
 * skips setting STORAGE_MODE = "on" — the whole migration is retried next boot.
 */
const migrateOffToOn = async (): Promise<void> => {
  const keys = await listKeys();
  for (const key of keys) {
    if (isSharedKey(key) || isTabScopedKey(key)) continue;

    const target = namespacedKey(key);
    if (target === key) continue; // nothing to do (flag off / already shared)

    try {
      const existingTarget = await sharedStorage.getItem(target);
      if (existingTarget == null) {
        const value = await sharedStorage.getItem(key);
        await sharedStorage.setItem(target, value);
      }
      await sharedStorage.removeItem(key);
    } catch (e) {
      // Leave the old key intact; STORAGE_MODE stays unset -> retry next boot.
      console.error("[migrateOffToOn] failed for key:", key, e);
      throw e;
    }
  }
};

/**
 * ON -> OFF: delete every tab-scoped key and reset identity (clean slate — we
 * don't promote any one tab's state to global). Shared keys (auth, RQ) stay.
 */
const migrateOnToOff = async (): Promise<void> => {
  const keys = await listKeys();
  await Promise.all(
    keys.filter(isTabScopedKey).map((key) => safeRemoveItem(key))
  );
  await safeRemoveItem(TAB_REGISTRY_KEY);
  resetTabIdentity();
};

const runModeTransition = async (mode: StorageModeEnum): Promise<void> => {
  const gotLock = await acquireMigrationLock(
    mode === StorageModeEnum.ON ? resolveTabId() : StorageModeEnum.OFF
  );
  try {
    if (mode === StorageModeEnum.ON) {
      await migrateOffToOn();
    } else {
      await migrateOnToOff();
    }
    await safeSetItem(STORAGE_MODE_KEY, mode);
  } finally {
    if (gotLock) await releaseMigrationLock();
  }
};

// ── Public API ──────────────────────────────────────────────────────────────
/**
 * Init the tab storage layer: claim an identity (forking it when this is a
 * duplicated tab), run the mode migration (if the flag changed since last boot),
 * set up liveness + cleanup, then open the readiness gate. Always resolves and
 * always opens the gate, so a failure can't block rehydration.
 */
export const initTabStorage = async (): Promise<void> => {
  try {
    const currentMode: StorageModeEnum = isTabIsolationEnabled
      ? StorageModeEnum.ON
      : StorageModeEnum.OFF;
    const lastMode = await readMode();

    // Must run before the migration: namespacing a key pins the tab id for the
    // rest of this load, and a duplicate would pin the source tab's id.
    const tabId = isTabIsolationEnabled ? await claimTabIdentity() : null;

    // First run (null) counts as a transition only when enabling.
    if (lastMode !== currentMode) {
      await runModeTransition(currentMode);
    }

    if (tabId) {
      await touchRegistry(tabId);
      startKeepAlive(tabId);
      setupPresenceChannel(tabId);
      scheduleBackgroundCleanup(tabId);
    }
  } catch (e) {
    console.error("[initTabStorage] initialisation failed:", e);
  } finally {
    tabStorageReadyGate.markReady();
  }
};

/**
 * Logout security boundary: delete every OTHER tab's per-tab keys and prune the
 * registry to this tab, preventing a stale rehydrate / cross-user leak on the
 * next login. Must-complete (awaited by logout). No-op when the feature is off.
 */
export const clearOtherTabStorages = async (): Promise<void> => {
  if (!isTabIsolationEnabled) return;

  const selfId = resolveTabId();
  const keys = await listKeys();
  const victims = keys.filter(
    (key) => isTabScopedKey(key) && !keyBelongsToTab(key, selfId)
  );
  await Promise.all(victims.map((key) => safeRemoveItem(key)));

  await safeSetItem<TabRegistry>(TAB_REGISTRY_KEY, { [selfId]: Date.now() });
};
